/**
 * Angular Reservation Input Component
 *
 * Migrated version of the AngularJS acme-reservation-input component.
 * Original: apps/b2b-ui/common/components/reservation-input/reservation-input.ts
 *
 * Numeric quantity input clamped between [min, max], with an optional "add-on"
 * badge previously shown when an inline reservation error was set.
 *
 * In the AngularJS version, the input change handler also called the
 * reservation API to create/update a temporary ticket reservation and
 * scheduled a $timeout to warn the parent before the reservation expired
 * (`onBeforeTimeout` / `onReservationError` / `onReservationSuccess`
 * outputs). That feature was disabled by ENG-1199 — the API call site was
 * commented out, leaving the reservation/timeout machinery dormant. This
 * migration drops the dormant timeout/$timeout code path but keeps
 * `reservationError` / `reservationSuccess` as declared outputs so consumer
 * templates can pre-wire their handlers; they will start emitting again
 * once ENG-1199 is reverted and the API orchestration is reintroduced on
 * top of ReservationsApiService. `onInputChange` is renamed to the
 * idiomatic `valueChange`.
 *
 * It implements ControlValueAccessor so it works with `[(ngModel)]` /
 * `formControl`, and also exposes a `value` two-way binding for use outside
 * of forms and for the AngularJS downgrade.
 *
 * Usage in Angular templates:
 *   <app-reservation-input [(value)]="qty" [max]="10" addOn="!"></app-reservation-input>
 *   <app-reservation-input [(ngModel)]="qty"></app-reservation-input>
 *
 * Usage in AngularJS templates (after downgrade):
 *   <ng-reservation-input value="$ctrl.qty" max="ticket.quantity" add-on="'!'"
 *                          on-value-change="$ctrl.handleQty($event)"
 *                          on-reservation-error="$ctrl.handleErr($event)"
 *                          on-reservation-success="$ctrl.handleOk($event)">
 *   </ng-reservation-input>
 */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnDestroy,
  OnInit,
  Output,
  SimpleChanges,
  forwardRef,
} from '@angular/core';
import { NgIf } from '@angular/common';
import { ControlValueAccessor, FormsModule, NG_VALUE_ACCESSOR } from '@angular/forms';
import { clamp } from 'lodash';

let uniqueId = 0;

@Component({
  selector: 'app-reservation-input',
  standalone: true,
  imports: [NgIf, FormsModule],
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => ReservationInputComponent),
      multi: true,
    },
  ],
  template: require('./reservation-input.component.html'),
  styles: [require('./reservation-input.component.less')],
})
export class ReservationInputComponent
  implements OnInit, OnChanges, OnDestroy, ControlValueAccessor
{
  /** Maximum allowed value (defaults to the legacy 99999 ceiling). */
  @Input() max = 99999;
  /** Minimum allowed value. */
  @Input() min = 0;
  /** Quantity to subtract before downstream reservation accounting. */
  @Input() ignoreValue = 0;
  /** Initial / externally-controlled value. */
  @Input() value = 0;
  /** Badge text shown next to the input when in an error state. */
  @Input() addOn = '';
  /** Owning event id (passed through to downstream handlers). */
  @Input() eventId = '';
  /** aria-label for the underlying <input>. */
  @Input() label = '';

  /** Emits the clamped numeric value whenever the user edits the input. */
  @Output() valueChange = new EventEmitter<number>();

  /**
   * Dormant — reserved for the future ENG-1199 re-enablement of the
   * reservation API call. Declared so consumer templates can pre-wire
   * `on-reservation-error="..."` handlers; nothing emits today.
   */
  @Output() reservationError = new EventEmitter<unknown>();

  /**
   * Dormant — reserved for the future ENG-1199 re-enablement of the
   * reservation API call. Will emit the created/updated reservation id
   * once the API orchestration is restored. Nothing emits today.
   */
  @Output() reservationSuccess = new EventEmitter<string>();

  /** Unique id linking the <input> for label/aria purposes. */
  readonly id = `acme-reservation-input-${++uniqueId}`;

  /** Inline error flag — preserved on the public surface for parity. */
  hasErrors = false;
  /** Whether the add-on badge is currently displayed. */
  showInputAddOn = false;

  /** The current clamped numeric value bound to the <input>. */
  currentValue = 0;
  /** Normalised min/max/addOn snapshot used by the template + change handler. */
  effectiveMax = 99999;
  effectiveMin = 0;
  effectiveIgnoreValue = 0;
  inputAddOn: string | null = null;

  /** 300ms debounce on user input, preserving the legacy ngModelOptions debounce. */
  private readonly debounceMs = 300;
  private debounceTimer: ReturnType<typeof setTimeout> | null = null;

  private onChangeFn: (value: number) => void = () => {};
  onTouched: () => void = () => {};

  ngOnInit(): void {
    this.recomputeFromInputs();
  }

  ngOnChanges(_changes: SimpleChanges): void {
    this.recomputeFromInputs();
  }

  ngOnDestroy(): void {
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
  }

  /** Bound to the <input>'s `(ngModelChange)`; debounces, clamps and emits. */
  onInputChange(next: number | string): void {
    this.currentValue = this.normalize(next);
    if (this.debounceTimer !== null) {
      clearTimeout(this.debounceTimer);
    }
    this.debounceTimer = setTimeout(() => {
      this.debounceTimer = null;
      this.commitChange();
    }, this.debounceMs);
  }

  // ── ControlValueAccessor ──────────────────────────────────────────────────
  writeValue(value: number): void {
    this.value = this.normalize(value);
    this.recomputeFromInputs();
  }

  registerOnChange(fn: (value: number) => void): void {
    this.onChangeFn = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  // ── internals ─────────────────────────────────────────────────────────────
  private recomputeFromInputs(): void {
    this.effectiveMax = Math.abs(this.normalize(this.max) || 99999);
    this.effectiveMin = Math.abs(this.normalize(this.min));
    this.effectiveIgnoreValue = Math.abs(this.normalize(this.ignoreValue));
    this.inputAddOn = this.addOn || null;
    this.currentValue = clamp(
      this.normalize(this.value),
      this.effectiveMin,
      this.effectiveMax,
    );
    this.showInputAddOn = !!(this.inputAddOn && this.hasErrors);
  }

  private commitChange(): void {
    const clamped = clamp(this.currentValue, this.effectiveMin, this.effectiveMax);
    this.currentValue = clamped;
    this.valueChange.emit(clamped);
    this.onChangeFn(clamped);
  }

  private normalize(value: unknown): number {
    return parseInt(String(value), 10) || 0;
  }
}
